import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";

export interface RecoveryResponse {
  success: boolean;
  message: string;
  data?: any;
}

interface RecoveryState {
  isLoading: boolean;
  emailOtpSent: boolean;
  smsCodeSent: boolean;
  emailVerified: boolean;
  smsVerified: boolean;

  sendRecoveryEmail: (agencyId: string, email: string) => Promise<void>;
  sendRecoverySms: (agencyId: string, phone: string) => Promise<void>;
  verifyRecoveryEmail: (agencyId: string, otp: string) => Promise<void>;
  verifyRecoverySms: (agencyId: string, code: string) => Promise<void>;
  resetState: () => void;
}

const useRecoveryStore = create<RecoveryState>((set, get) => ({
  isLoading: false,
  emailOtpSent: false,
  smsCodeSent: false,
  emailVerified: false,
  smsVerified: false,

  sendRecoveryEmail: async (agencyId: string, email: string) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post<RecoveryResponse>(
        `/agency/${agencyId}/send-recovery-email`,
        { email }
      );

      if (response.data.success) {
        set({ emailOtpSent: true, isLoading: false });
      } else {
        set({ isLoading: false });
        throw new Error(response.data.message || "Failed to send recovery email");
      }
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to send recovery email";
      throw new Error(errorMessage);
    }
  },

  sendRecoverySms: async (agencyId: string, phone: string) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post<RecoveryResponse>(
        `/agency/${agencyId}/send-recovery-sms`,
        { phone }
      );

      if (response.data.success) {
        set({ smsCodeSent: true, isLoading: false });
      } else {
        set({ isLoading: false });
        throw new Error(response.data.message || "Failed to send recovery SMS");
      }
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to send recovery SMS";
      throw new Error(errorMessage);
    }
  },

  verifyRecoveryEmail: async (agencyId: string, otp: string) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post<RecoveryResponse>(
        `/agency/${agencyId}/verify-recovery-email`,
        { otp }
      );

      if (response.data.success) {
        set({ emailVerified: true, isLoading: false });
      } else {
        set({ isLoading: false });
        throw new Error(response.data.message || "Failed to verify email OTP");
      }
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to verify email OTP";
      throw new Error(errorMessage);
    }
  },

  verifyRecoverySms: async (agencyId: string, code: string) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post<RecoveryResponse>(
        `/agency/${agencyId}/verify-recovery-sms`,
        { code }
      );

      if (response.data.success) {
        set({ smsVerified: true, isLoading: false });
      } else {
        set({ isLoading: false });
        throw new Error(response.data.message || "Failed to verify SMS code");
      }
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to verify SMS code";
      throw new Error(errorMessage);
    }
  },

  resetState: () => {
    set({
      isLoading: false,
      emailOtpSent: false,
      smsCodeSent: false,
      emailVerified: false,
      smsVerified: false,
    });
  },
}));

export default useRecoveryStore;
